import * as moment from 'moment';
import * as _ from 'underscore';

import { Injectable } from '@angular/core';

import { ColorPalette, Partner } from '../../analytics-core/interfaces/partner.interface';
import { PERMISSION } from '../../analytics-core/interfaces/permission.interface';
import { Advertiser } from '../interfaces/advertiser.interface';
import { Campaign } from '../interfaces/campaign.interface';
import { SetupData } from '../interfaces/setupdata.interface';
import { User } from '../interfaces/user.interface';
import { Mixpanel } from '../interfaces/mixpanel.interface';

@Injectable()
export class AnalyticsMapperService {
	mapSetupDataToInterface(setupResponseData: any): SetupData {
		const setupData: SetupData = {} as SetupData;
		setupData.user = this.mapUserDataToInterface(setupResponseData.user);
		setupData.mixpanel = this.mapMixpanelDataToInterface(setupResponseData.mixpanel);
		setupData.partner = this.mapPartnerDataToInterface(setupResponseData.partner);
		setupData.advertisers = this.mapAdvertisersDataToInterface(setupResponseData.advertisers);
		setupData.userPermissions = this.mapUserPermissionDataToInterfac(setupResponseData.permission);
		setupData.depricatedDate = setupResponseData.deprecated_date_avg_position;
		setupData.goLiveDate = setupResponseData.go_live_date_impression_share;
		const permissions = [];
		for (const key in setupData.userPermissions) {
			if (setupData.userPermissions[key]) {
				permissions.push(key);
			}
		}
		setupData.permissions = permissions;
		return setupData;
	}

	mapMixpanelDataToInterface(mixpanelResponseData: any): Mixpanel {
		const mixpanel: Mixpanel = {} as Mixpanel;
		mixpanel.u_id = mixpanelResponseData.user_unique_id;
		mixpanel.firstName = mixpanelResponseData.user_firstname;
		mixpanel.lastName = mixpanelResponseData.user_lastname;
		mixpanel.email = mixpanelResponseData.user_email;
		mixpanel.user_role = mixpanelResponseData.user_role;
		mixpanel.user_is_super = mixpanelResponseData.user_is_super;
		mixpanel.user_partner_id = mixpanelResponseData.user_partner_id;
		mixpanel.user_partner = mixpanelResponseData.user_partner;
		mixpanel.user_cname = mixpanelResponseData.user_cname;
		mixpanel.user_advertiser_id = mixpanelResponseData.user_advertiser_id;
		mixpanel.user_advertiser_name = mixpanelResponseData.user_advertiser_name;
		mixpanel.page_access_time = mixpanelResponseData.page_access_time;
		mixpanel.mixpanel_token = mixpanelResponseData.mixpanel_token;
		return mixpanel;
	}

	mapPartnerDataToInterface(partnerResponseData: any): Partner {
		const partner: Partner = {} as Partner;
		partner.partnerName = partnerResponseData.name;
		partner.partnerLogo = partnerResponseData.logo;
		partner.headerLogo = partnerResponseData.header_image;
		partner.isMarketron = partnerResponseData.ismarketron;
		partner.favIcon = partnerResponseData.favicon_path;
		partner.analyticsConfig = partnerResponseData.analytics_config;
		partner.mapboxId = partnerResponseData.mapbox_id;
		partner.countryDetails = partnerResponseData.country_details;
		partner.colorPalette = this.mapColorPaletteDataToInterface(partnerResponseData.color_palette[0]);
		return partner;
	}

	/**
	 *
	 * @param userPermissionData
	 * @returns {{PERMISSION: boolean}}
	 */
	mapUserPermissionDataToInterfac(userPermissionData: any): { PERMISSION: boolean } {
		const userPermissionObj: { PERMISSION: boolean } = {} as { PERMISSION: boolean };
		userPermissionObj[PERMISSION[PERMISSION.hasDisplay]] = userPermissionData.has_display_access;
		userPermissionObj[PERMISSION[PERMISSION.hasPreroll]] = userPermissionData.has_preroll_access;
		userPermissionObj[PERMISSION[PERMISSION.hasSem]] = userPermissionData.has_sem_access;
		userPermissionObj[PERMISSION[PERMISSION.hasGeoFence]] = userPermissionData.has_geofence_access;
		userPermissionObj[PERMISSION[PERMISSION.hasGeoVideo]] = userPermissionData.has_geovideo_access;
		userPermissionObj[PERMISSION[PERMISSION.hasCtv]] = userPermissionData.has_ctv_access;
		userPermissionObj[PERMISSION[PERMISSION.hasSocial]] = userPermissionData.has_social_access;
		userPermissionObj[PERMISSION[PERMISSION.hasTelevision]] = userPermissionData.has_television_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasRenewalStrategy]] = userPermissionData.has_renewal_strategy_access;
		userPermissionObj[PERMISSION[PERMISSION.hasRadio]] = userPermissionData.has_radio_access ?? false;
		userPermissionObj[PERMISSION[PERMISSION.hasSimplifiCTV]] = userPermissionData.has_simplifi_ctv_access ?? false;
		userPermissionObj[PERMISSION[PERMISSION.showPlacements]] = userPermissionData.show_placement_widget;
		userPermissionObj[PERMISSION[PERMISSION.hasOfflineVisits]] = userPermissionData.has_offline_visits_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasProgrammaticsProductsOfflineVisits]] =
			userPermissionData.has_programmatic_products_offline_visits_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasEmail]] = userPermissionData.has_email_access;
		userPermissionObj[PERMISSION[PERMISSION.hasRadioTriton]] = userPermissionData.has_triton_radio_access;
		userPermissionObj[PERMISSION[PERMISSION.hasResponsiveDisplay]] = userPermissionData.has_responsive_display_access;
		userPermissionObj[PERMISSION[PERMISSION.hasGAM]] = userPermissionData.has_gam_access;
		userPermissionObj[PERMISSION[PERMISSION.hasStandaloneGAM]] = userPermissionData.has_standalone_gam;
		userPermissionObj[PERMISSION[PERMISSION.hasDOOH]] = userPermissionData.has_dooh_access;
		userPermissionObj[PERMISSION[PERMISSION.hasDOOHVideo]] = userPermissionData.has_dooh_video_access;
		userPermissionObj[PERMISSION[PERMISSION.hasGamMapAccess]] = userPermissionData.has_gam_map_access;
		userPermissionObj[PERMISSION[PERMISSION.hasNoGamMapAccess]] = !userPermissionData.has_gam_map_access;
		userPermissionObj[PERMISSION[PERMISSION.hasKeywordsTargeting]] =
			userPermissionData.has_keyword_target_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasSEMPrintableAccess]] =
			userPermissionData.has_sem_printable_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasTelevisionPrintableAccess]] =
			userPermissionData.has_tv_printable_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasDisplayPrintableAccess]] =
			userPermissionData.has_display_printable_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasGAMPrintableAccess]] =
			userPermissionData.has_gam_printable_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasPrerollPrintableAccess]] =
			userPermissionData.has_video_printable_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasGeofencePrintableAccess]] =
			userPermissionData.has_geofence_printable_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasGeovideoPrintableAccess]] =
			userPermissionData.has_geovideo_printable_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasStreaminAudioPrintableAccess]] =
			userPermissionData.has_streaming_audio_printable_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasTritonPrintableAccess]] =
			userPermissionData.has_triton_printable_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasCtvPrintableAccess]] =
			userPermissionData.has_ctv_printable_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasPrerollCompletionAccess]] =
			userPermissionData.has_preroll_completion_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasSocialPrintableAccess]] =
			userPermissionData.has_social_printable_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasHotjarAccess]] = userPermissionData.has_hotjar_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasSimplifiCTVPrintableAccess]] =
			userPermissionData.has_simplifi_ctv_printable_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasYoutubeViews]] = userPermissionData.has_youtube_views_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasSEMConversionsAccess]] =
			userPermissionData.has_sem_conversions_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasYoutubeCostAccess]] =
			userPermissionData.has_youtube_cost_transparency_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasSocialMediaCostAccess]] =
			userPermissionData.has_social_media_cost_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasEmailPrintableAccess]] =
			userPermissionData.has_email_printable_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasResponsiveDisplayPrintableAccess]] =
			userPermissionData.has_responsive_display_printable_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasDisplayLineItemsAccess]] =
			userPermissionData.has_gam_display_lineitem_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasGamLineitemAccess]] =
			userPermissionData.has_gam_lineitem_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasGamScreenshotAccess]] =
			userPermissionData.has_gam_screenshot_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasPrerollLineItemsAccess]] =
			userPermissionData.has_gam_preroll_lineitem_access || false;
		userPermissionObj[PERMISSION[PERMISSION.customizeAdvertiserLimit]] =
			userPermissionData.customize_Advertiser_Limit || false;
		userPermissionObj[PERMISSION[PERMISSION.customizeCampaignLimit]] =
			userPermissionData.customize_campaign_limit || false;
		userPermissionObj[PERMISSION[PERMISSION.resourceLibrary]] = userPermissionData.resource_library || false;
		userPermissionObj[PERMISSION[PERMISSION.hasOverviewPrintableAccess]] =
			userPermissionData.has_overview_printable_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasOverviewCSVAccess]] =
			userPermissionData.has_overview_csv_access || false;
		userPermissionObj[PERMISSION[PERMISSION.hasDOOHPrintableAccess]] =
			userPermissionData.has_dooh_printable_access || false;
		userPermissionObj[PERMISSION[PERMISSION.toRemoveProductInNavigation]] = false; // Permission to remove the widget from configuration
		userPermissionObj[PERMISSION[PERMISSION.hideItem]] = false;
		userPermissionObj[PERMISSION[PERMISSION.hasLookerPOC]] = userPermissionData.has_looker_poc_access;

		return userPermissionObj;
	}

	mapColorPaletteDataToInterface(colorPaletteData: any): ColorPalette {
		const colorPalette: ColorPalette = {} as ColorPalette;
		if (colorPaletteData) {
			colorPalette.primaryColor = colorPaletteData.primary_color;
			colorPalette.secondaryColor = colorPaletteData.secondary_color;
			colorPalette.tertiaryColor = colorPaletteData.tertiary_color;
			colorPalette.quaternaryColor = colorPaletteData.quaternary_color;
			colorPalette.quinaryColor = colorPaletteData.quinary_color;
		}
		return colorPalette;
	}

	mapUserDataToInterface(userResponseData: any): User {
		const user: User = {} as User;
		user.firstName = userResponseData.firstname;
		user.lastName = userResponseData.lastname;
		user.email = userResponseData.email;
		return user;
	}

	mapAdvertisersDataToInterface(advertisersResponse: any): Advertiser[] {
		const advertisers: Advertiser[] = [];
		for (const advertiserResponse of advertisersResponse) {
			advertisers.push(this.mapAdvertiserDataToInterface(advertiserResponse));
		}
		return advertisers;
	}

	mapAdvertiserDataToInterface(advertiserResponseData: any): Advertiser {
		const advertiser: Advertiser = {} as Advertiser;
		advertiser.advertiserId = advertiserResponseData.id;
		advertiser.id = advertiserResponseData.id;
		advertiser.advertiserName = advertiserResponseData.Name;
		advertiser.isDemoAdvertiser = this.toTrueOrFalse(advertiserResponseData.is_demo);
		advertiser.isTTDEnabled = this.toTrueOrFalse(advertiserResponseData.is_ttd_enabled);
		advertiser.selected = false;
		return advertiser;
	}

	mapCampaignsDataToInterface(campaignsResponse: any): Campaign[] {
		const campaigns: Campaign[] = [];
		for (const campaignResponse of campaignsResponse) {
			campaigns.push(this.mapCampaignDataToInterface(campaignResponse));
		}
		return campaigns;
	}

	mapCampaignDataToInterface(campaignResponseData: any): Campaign {
		const campaign: Campaign = {} as Campaign;
		campaign.id = campaignResponseData.id;
		campaign.campaignId = campaignResponseData.id;
		campaign.campaignName = campaignResponseData.Name;
		campaign.adv_name = campaignResponseData.adv_name;
		campaign.permission = campaignResponseData.permission;
		campaign.selected = false;
		campaign.isDisplay = campaignResponseData.is_display === 'true';
		campaign.active = !(campaignResponseData.disabled === 'true');
		campaign.isGAM = campaignResponseData.is_gam === 'true';
		campaign.activePermissionsWithData = campaignResponseData.permission_impressions;
		return campaign;
	}

	mapCampaignsDropdownSelectionDataToInterface(campaignsResponse: any[]): any[] {
		const campaigns: Campaign[] = [];
		for (const campaignResponse of campaignsResponse) {
			const campaign: Campaign = { ...{}, ...campaignResponse } as Campaign;
			campaign.campaignId = campaignResponse.id;
			campaign.campaignName = campaignResponse.itemName;
			campaign.adv_name = campaignResponse.adv_name;
			campaign.permission = campaignResponse.permission;
			campaign.selected = true;
			campaign.isDisplay = campaignResponse.isDisplay;
			campaign.active = campaignResponse.active;
			campaign.isGAM = campaignResponse.isGAM;
			campaign.activePermissionsWithData = campaignResponse.activePermissionsWithData;
			campaigns.push(campaign);
		}
		return campaigns;
	}

	toTrueOrFalse(value: string): any {
		if (typeof value !== 'boolean') {
			return !!parseInt(value, 10);
		}
		return value;
	}

	mapAdvertisersFromDropdown(advertisers: Advertiser[]): Advertiser[] {
		const dropdownAdvertisers: any[] = [];
		for (const advertiser of advertisers) {
			const temp: any = {};
			temp.id = advertiser.advertiserId;
			temp.itemName = advertiser.advertiserName;
			temp.is_demo = advertiser.isDemoAdvertiser;
			temp.selected = false;
			dropdownAdvertisers.push(temp);
		}
		return dropdownAdvertisers;
	}

	mapSelectedAdvertisersFromDropdown(advertisers: Advertiser[]): Advertiser[] {
		const dropdownAdvertisers: any[] = [];

		for (const advertiser of advertisers) {
			if (advertiser.selected) {
				const temp: any = {};
				temp.id = advertiser.advertiserId;
				temp.itemName = advertiser.advertiserName;
				temp.is_demo = advertiser.isDemoAdvertiser;
				dropdownAdvertisers.push(temp);
			}
		}
		return dropdownAdvertisers;
	}

	checkDemoAdvertisers(advertisers: Advertiser[]): boolean {
		let returnType = false;
		for (const advertiser of advertisers) {
			if (advertiser.isDemoAdvertiser) {
				returnType = true;
				break;
			}
		}
		return returnType;
	}

	mapCampaignsFromDropdown(campaigns: Campaign[]): Campaign[] {
		const dropdownCampaigns: any[] = [];
		for (const campaign of campaigns) {
			const temp: any = {};
			temp.id = campaign.campaignId;
			temp.itemName = campaign.campaignName;
			temp.adv_name = campaign.adv_name;
			temp.isDisplay = campaign.isDisplay;
			temp.selected = campaign.selected;
			temp.permission = campaign.permission;
			temp.active = campaign.active;
			temp.isGAM = campaign.isGAM;
			temp.activePermissionsWithData = campaign.activePermissionsWithData;
			dropdownCampaigns.push(temp);
		}
		return dropdownCampaigns;
	}

	mapSelectedCampaignsFromDropdown(campaigns: Campaign[]): Campaign[] {
		const dropdownCampaigns: any[] = [];
		for (const campaign of campaigns) {
			const temp: any = {};
			if (campaign.selected) {
				temp.id = campaign.campaignId;
				temp.itemName = campaign.campaignName;
				temp.adv_name = campaign.adv_name;
				temp.isDisplay = campaign.isDisplay;
				temp.permission = campaign.permission;
				temp.active = campaign.active;
				temp.isGAM = campaign.isGAM;
				temp.activePermissionsWithData = campaign.activePermissionsWithData;
				dropdownCampaigns.push(temp);
			}
		}
		return dropdownCampaigns;
	}

	mapInterfaceToRequestObject(
		advertisers: _.List<any>,
		campaigns: _.List<any>,
		startDate: moment.MomentInput,
		endDate: moment.MomentInput
	): object {
		var tempGamCampaigns = [];
		var tempNonGamCampaigns = [];
		if (campaigns?.length > 0) {
			for (let i = 0; i < campaigns.length; i++) {
				if (campaigns[i].isGAM) {
					tempGamCampaigns.push(campaigns[i]);
				} else {
					tempNonGamCampaigns.push(campaigns[i]);
				}
			}
		}
		const aIds = _.pluck(advertisers, 'id');
		const cIds = _.pluck(campaigns, 'id');
		const sDate = moment(startDate).format('YYYY-MM-DD');
		const eDate = moment(endDate).format('YYYY-MM-DD');
		const gamIds = _.pluck(tempGamCampaigns, 'id');
		const nonGamIds = _.pluck(tempNonGamCampaigns, 'id');
		return { start_date: sDate, end_date: eDate, c_ids: cIds, a_ids: aIds, gam_ids: gamIds, non_gam_ids: nonGamIds };
	}
}
